import { create } from 'zustand'
import type { Card, CardType, LessonDoc, LessonMeta, RichCard } from '../types/LessonDoc'
import type { ContentItem } from '../../../types'
import { genId, slugify } from '../lib/id'
import { createDefaultCard } from '../cards/registry'
import { makeDefaultSections, makeSection, normalizeLesson } from '../lib/sections'

interface LessonBuilderState {
  lesson: LessonDoc
  selectedSectionId: string | null
  selectedCardId: string | null

  // Bài học
  newLesson: () => void
  loadLesson: (doc: any) => void
  setMeta: (patch: Partial<LessonMeta>) => void

  // Phần (hoạt động sư phạm)
  addSection: (title?: string) => void
  renameSection: (id: string, title: string) => void
  patchSection: (id: string, patch: Partial<import('../types/LessonDoc').LessonSection>) => void
  removeSection: (id: string) => void
  moveSection: (id: string, dir: -1 | 1) => void
  reorderSection: (id: string, toIndex: number) => void
  selectSection: (id: string | null) => void

  // Thẻ
  addCard: (sectionId: string, type: CardType) => void
  addRichCard: (items: ContentItem[], title?: string) => void
  addPreparedCard: (card: Card, prefer?: RegExp) => void
  updateCard: (cardId: string, patch: Partial<Card>) => void
  removeCard: (cardId: string) => void
  moveCard: (cardId: string, dir: -1 | 1) => void
  moveCardToSection: (cardId: string, toSectionId: string) => void
  selectCard: (id: string | null) => void
}

function emptyLesson(): LessonDoc {
  const sections = makeDefaultSections()
  return {
    id: genId('lesson'),
    slug: '',
    meta: { tieuDe: '' },
    sections,
    status: 'draft',
  }
}

export const useLessonBuilder = create<LessonBuilderState>((set) => ({
  lesson: emptyLesson(),
  selectedSectionId: null,
  selectedCardId: null,

  newLesson: () => {
    const lesson = emptyLesson()
    set({ lesson, selectedSectionId: lesson.sections[0]?.id ?? null, selectedCardId: null })
  },

  loadLesson: (doc) => {
    const lesson = normalizeLesson(doc)
    set({
      lesson,
      selectedSectionId: lesson.sections[0]?.id ?? null,
      selectedCardId: lesson.sections[0]?.cards[0]?.id ?? null,
    })
  },

  setMeta: (patch) =>
    set((s) => {
      const meta = { ...s.lesson.meta, ...patch }
      // Slug bám TIÊU ĐỀ ĐẦY ĐỦ mỗi khi tiêu đề đổi (KHÔNG khoá theo ký tự đầu → tránh slug '/s').
      // Không đổi tiêu đề thì giữ slug hiện tại (giữ slug đã uniquify từ server khi sửa field khác).
      const slug = patch.tieuDe !== undefined ? slugify(patch.tieuDe) : s.lesson.slug
      return { lesson: { ...s.lesson, meta, slug } }
    }),

  // ---- Phần ----
  addSection: (title) =>
    set((s) => {
      const sec = makeSection(title || `Phần ${s.lesson.sections.length + 1}`)
      return { lesson: { ...s.lesson, sections: [...s.lesson.sections, sec] }, selectedSectionId: sec.id }
    }),

  renameSection: (id, title) =>
    set((s) => ({
      lesson: {
        ...s.lesson,
        sections: s.lesson.sections.map((sec) => (sec.id === id ? { ...sec, title } : sec)),
      },
    })),

  patchSection: (id, patch) =>
    set((s) => ({
      lesson: {
        ...s.lesson,
        sections: s.lesson.sections.map((sec) => (sec.id === id ? { ...sec, ...patch } : sec)),
      },
    })),

  removeSection: (id) =>
    set((s) => {
      const sections = s.lesson.sections.filter((sec) => sec.id !== id)
      return {
        lesson: { ...s.lesson, sections },
        selectedSectionId: s.selectedSectionId === id ? sections[0]?.id ?? null : s.selectedSectionId,
      }
    }),

  moveSection: (id, dir) =>
    set((s) => {
      const arr = [...s.lesson.sections]
      const i = arr.findIndex((x) => x.id === id)
      const j = i + dir
      if (i < 0 || j < 0 || j >= arr.length) return s
      ;[arr[i], arr[j]] = [arr[j], arr[i]]
      return { lesson: { ...s.lesson, sections: arr } }
    }),

  reorderSection: (id, toIndex) =>
    set((s) => {
      const arr = [...s.lesson.sections]
      const from = arr.findIndex((x) => x.id === id)
      if (from < 0) return s
      let to = Math.max(0, Math.min(arr.length - 1, toIndex))
      const [moved] = arr.splice(from, 1)
      // sau khi rút phần tử, nếu thả xuống dưới thì chỉ số đích giữ nguyên (splice đã dịch)
      if (from < to) to -= 1
      arr.splice(to, 0, moved)
      return { lesson: { ...s.lesson, sections: arr } }
    }),

  selectSection: (id) => set({ selectedSectionId: id }),

  // ---- Thẻ ----
  addCard: (sectionId, type) =>
    set((s) => {
      const card = createDefaultCard(type)
      return {
        lesson: {
          ...s.lesson,
          sections: s.lesson.sections.map((sec) =>
            sec.id === sectionId ? { ...sec, cards: [...sec.cards, card] } : sec
          ),
        },
        selectedSectionId: sectionId,
        selectedCardId: card.id,
      }
    }),

  // Word → Bài học AI: thêm thẻ rich vào "Hoạt động chính" (hoặc phần đầu nếu không có)
  addRichCard: (items, title) =>
    set((s) => {
      const card: RichCard = { id: genId(), type: 'rich', items, title }
      const target =
        s.lesson.sections.find((sec) => /chính/i.test(sec.title)) || s.lesson.sections[0]
      let sections = s.lesson.sections
      if (!target) {
        const sec = makeSection('Hoạt động chính', [card])
        sections = [...sections, sec]
      } else {
        sections = sections.map((sec) =>
          sec.id === target.id ? { ...sec, cards: [...sec.cards, card] } : sec
        )
      }
      const meta = s.lesson.meta.tieuDe?.trim()
        ? s.lesson.meta
        : { ...s.lesson.meta, tieuDe: title || s.lesson.meta.tieuDe }
      return { lesson: { ...s.lesson, meta, sections }, selectedCardId: card.id }
    }),

  // Gắn 1 thẻ đã dựng sẵn vào phần (mặc định phần khớp `prefer`, hoặc phần đầu)
  addPreparedCard: (card, prefer) =>
    set((s) => {
      const target =
        (prefer && s.lesson.sections.find((sec) => prefer.test(sec.title))) || s.lesson.sections[0]
      if (!target) return s
      const sections = s.lesson.sections.map((sec) =>
        sec.id === target.id ? { ...sec, cards: [...sec.cards, card] } : sec
      )
      return { lesson: { ...s.lesson, sections } }
    }),

  updateCard: (cardId, patch) =>
    set((s) => ({
      lesson: {
        ...s.lesson,
        sections: s.lesson.sections.map((sec) => ({
          ...sec,
          cards: sec.cards.map((c) => (c.id === cardId ? ({ ...c, ...patch } as Card) : c)),
        })),
      },
    })),

  removeCard: (cardId) =>
    set((s) => ({
      lesson: {
        ...s.lesson,
        sections: s.lesson.sections.map((sec) => ({
          ...sec,
          cards: sec.cards.filter((c) => c.id !== cardId),
        })),
      },
      selectedCardId: s.selectedCardId === cardId ? null : s.selectedCardId,
    })),

  moveCard: (cardId, dir) =>
    set((s) => ({
      lesson: {
        ...s.lesson,
        sections: s.lesson.sections.map((sec) => {
          const i = sec.cards.findIndex((c) => c.id === cardId)
          if (i < 0) return sec
          const j = i + dir
          if (j < 0 || j >= sec.cards.length) return sec
          const cards = [...sec.cards]
          ;[cards[i], cards[j]] = [cards[j], cards[i]]
          return { ...sec, cards }
        }),
      },
    })),

  // Kéo thẻ sang phần khác (thả vào cuối phần đích)
  moveCardToSection: (cardId, toSectionId) =>
    set((s) => {
      let moved: Card | undefined
      const stripped = s.lesson.sections.map((sec) => {
        const found = sec.cards.find((c) => c.id === cardId)
        if (found) moved = found
        return { ...sec, cards: sec.cards.filter((c) => c.id !== cardId) }
      })
      if (!moved) return s
      const sections = stripped.map((sec) =>
        sec.id === toSectionId ? { ...sec, cards: [...sec.cards, moved as Card] } : sec
      )
      return { lesson: { ...s.lesson, sections }, selectedSectionId: toSectionId, selectedCardId: cardId }
    }),

  selectCard: (id) => set({ selectedCardId: id }),
}))
